import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { NeedsSetup } from "@/components/NeedsSetup";
import { BidPanel } from "@/components/BidPanel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Building2, Plus, Briefcase, Calendar, IndianRupee, 
  CheckCircle, Loader2, Users, Scale, Sparkles, ThumbsUp, ExternalLink,
  Globe, Linkedin, Twitter
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Builders = () => {
  const [searchParams] = useSearchParams();
  const societyIdFromUrl = searchParams.get('society');
  
  const [builders, setBuilders] = useState([]);
  const [consentStatus, setConsentStatus] = useState(null);
  const [society, setSociety] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [givingConsent, setGivingConsent] = useState(null);
  const [isAdminManaging, setIsAdminManaging] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    rera_number: "",
    contact_person: "",
    phone: "",
    email: "",
    experience_years: "",
    past_projects: "",
    ongoing_projects: "",
    offer_details: "",
    corpus_amount: "",
    rent_amount: "",
    area_ratio: "",
    timeline_months: "",
    website: "",
    linkedin: ""
  });

  useEffect(() => {
    fetchData();
  }, [societyIdFromUrl]);

  const fetchData = async () => {
    try {
      let societyId;
      
      // If society ID is in URL (admin managing), use that
      if (societyIdFromUrl) {
        const socRes = await fetch(`${API}/societies/${societyIdFromUrl}`, { credentials: "include" });
        if (socRes.ok) {
          const soc = await socRes.json();
          setSociety(soc);
          societyId = societyIdFromUrl;
          setIsAdminManaging(true);
        } else {
          toast.error("Society not found or access denied");
          setLoading(false);
          return;
        }
      } else {
        // Normal flow
        const socRes = await fetch(`${API}/societies`, { credentials: "include" });
        const societies = await socRes.json();
        
        if (societies.length > 0) {
          setSociety(societies[0]);
          societyId = societies[0].society_id;
        }
      }
      
      if (societyId) {
        const [buildersRes, consentRes] = await Promise.all([
          fetch(`${API}/societies/${societyId}/builders`, { credentials: "include" }),
          fetch(`${API}/societies/${societyId}/consent-status`, { credentials: "include" })
        ]);
        
        if (buildersRes.ok) setBuilders(await buildersRes.json());
        if (consentRes.ok) setConsentStatus(await consentRes.json());
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddBuilder = async (e) => {
    e.preventDefault();
    if (!society) return;

    setSaving(true);
    toast.info("Adding builder... AI will fetch additional information if available.", { duration: 3000 });
    
    try {
      const payload = {
        name: formData.name,
        company: formData.company,
        rera_number: formData.rera_number || null,
        contact_person: formData.contact_person || null,
        phone: formData.phone || null,
        email: formData.email || null,
        experience_years: parseInt(formData.experience_years) || 0,
        past_projects: parseInt(formData.past_projects) || 0,
        ongoing_projects: parseInt(formData.ongoing_projects) || 0,
        offer_details: formData.offer_details,
        corpus_amount: formData.corpus_amount ? parseFloat(formData.corpus_amount) : null,
        rent_amount: formData.rent_amount ? parseFloat(formData.rent_amount) : null,
        area_ratio: formData.area_ratio || null,
        timeline_months: formData.timeline_months ? parseInt(formData.timeline_months) : null
      };

      const res = await fetch(`${API}/societies/${society.society_id}/builders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });

      if (res.ok) {
        const newBuilder = await res.json();
        setBuilders([...builders, newBuilder]);
        toast.success("Builder added successfully");
        if (newBuilder.ai_fetched_info) {
          toast.info("AI fetched additional builder information");
        }
        setDialogOpen(false);
        setFormData({
          name: "", company: "", rera_number: "", contact_person: "", phone: "", email: "",
          experience_years: "", past_projects: "", ongoing_projects: "", offer_details: "",
          corpus_amount: "", rent_amount: "", area_ratio: "", timeline_months: ""
        });
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to add builder");
      }
    } catch (error) {
      toast.error("Failed to add builder");
    } finally {
      setSaving(false);
    }
  };

  const handleGiveConsent = async (builderId) => {
    setGivingConsent(builderId);
    try {
      const res = await fetch(`${API}/societies/${society.society_id}/consent?builder_id=${builderId}`, {
        method: "POST",
        credentials: "include"
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`Consent recorded! Current: ${result.consent_percentage.toFixed(1)}%`);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to give consent");
      }
    } catch (error) {
      toast.error("Failed to give consent");
    } finally {
      setGivingConsent(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!society) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <NeedsSetup message="Set up your society to compare and select builders." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Builder Bidding & Consent
            </h1>
            <p className="text-slate-600">Manage builder proposals and member consent (EOA)</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-builder-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Builder
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>Add Builder for Bidding</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddBuilder} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company">Company Name *</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="e.g., ABC Developers Pvt Ltd"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="rera_number">RERA Registration No.</Label>
                    <Input
                      id="rera_number"
                      name="rera_number"
                      value={formData.rera_number}
                      onChange={handleChange}
                      placeholder="e.g., P51900001234"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Contact Person *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., Mr. Rajesh Sharma"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="e.g., 9876543210"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="experience_years">Experience (Years)</Label>
                    <Input
                      id="experience_years"
                      name="experience_years"
                      type="number"
                      value={formData.experience_years}
                      onChange={handleChange}
                      placeholder="e.g., 15"
                    />
                  </div>
                  <div>
                    <Label htmlFor="past_projects">Completed Projects</Label>
                    <Input
                      id="past_projects"
                      name="past_projects"
                      type="number"
                      value={formData.past_projects}
                      onChange={handleChange}
                      placeholder="e.g., 25"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ongoing_projects">Ongoing Projects</Label>
                    <Input
                      id="ongoing_projects"
                      name="ongoing_projects"
                      type="number"
                      value={formData.ongoing_projects}
                      onChange={handleChange}
                      placeholder="e.g., 5"
                    />
                  </div>
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-slate-700 mb-3">Offer Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="area_ratio">Area Ratio Offered</Label>
                      <Input
                        id="area_ratio"
                        name="area_ratio"
                        value={formData.area_ratio}
                        onChange={handleChange}
                        placeholder="e.g., 1:1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="timeline_months">Timeline (Months)</Label>
                      <Input
                        id="timeline_months"
                        name="timeline_months"
                        type="number"
                        value={formData.timeline_months}
                        onChange={handleChange}
                        placeholder="e.g., 36"
                      />
                    </div>
                    <div>
                      <Label htmlFor="corpus_amount">Corpus Amount (₹ Lakhs)</Label>
                      <Input
                        id="corpus_amount"
                        name="corpus_amount"
                        type="number"
                        value={formData.corpus_amount}
                        onChange={handleChange}
                        placeholder="e.g., 25"
                      />
                    </div>
                    <div>
                      <Label htmlFor="rent_amount">Monthly Rent (₹)</Label>
                      <Input
                        id="rent_amount"
                        name="rent_amount"
                        type="number"
                        value={formData.rent_amount}
                        onChange={handleChange}
                        placeholder="e.g., 30000"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="offer_details">Detailed Offer Description *</Label>
                  <Textarea
                    id="offer_details"
                    name="offer_details"
                    value={formData.offer_details}
                    onChange={handleChange}
                    placeholder="Describe the complete offer including amenities, parking, etc."
                    rows={4}
                    required
                  />
                </div>
                
                <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                  <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                  <p className="text-sm text-blue-700">
                    AI will automatically fetch additional builder information from online sources if available.
                  </p>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-700 hover:bg-emerald-800"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding Builder...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Builder
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Consent Progress */}
        {consentStatus && (
          <Card className="bg-gradient-to-r from-emerald-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Members</p>
                  <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                    {consentStatus.total_members}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">Required Consent</p>
                  <p className="text-3xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope' }}>
                    {consentStatus.threshold}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Work Execution Bidding Panel */}
        {society && (
          <BidPanel
            societyId={society.society_id}
            bidType="execution"
            candidates={builders}
            idKey="builder_id"
            statusPath="builders"
            onWinnerSelected={fetchData}
          />
        )}

        {/* Builders List */}
        {builders.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-emerald-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                No Builders Added
              </h3>
              <p className="text-slate-600 max-w-md mx-auto mb-6">
                Add builder candidates who have submitted proposals for your society's redevelopment.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {builders.map((builder) => (
              <Card key={builder.builder_id} className="overflow-hidden">
                <CardHeader className="bg-slate-50 pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{builder.company}</CardTitle>
                      <CardDescription>{builder.name}</CardDescription>
                    </div>
                    {builder.rera_number && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        RERA: {builder.rera_number}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-lg font-bold text-slate-900">{builder.experience_years || '-'}</p>
                      <p className="text-xs text-slate-500">Years Exp.</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-lg font-bold text-slate-900">{builder.past_projects || '-'}</p>
                      <p className="text-xs text-slate-500">Projects</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-lg font-bold text-slate-900">{builder.timeline_months || '-'}</p>
                      <p className="text-xs text-slate-500">Months</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-sm">
                    {builder.area_ratio && (
                      <span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded">
                        Ratio: {builder.area_ratio}
                      </span>
                    )}
                    {builder.corpus_amount && (
                      <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded">
                        ₹{builder.corpus_amount}L corpus
                      </span>
                    )}
                    {builder.rent_amount && (
                      <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">
                        ₹{builder.rent_amount.toLocaleString()}/mo rent
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-slate-600 line-clamp-2">{builder.offer_details}</p>

                  {builder.ai_fetched_info && (
                    <div className="p-2 bg-purple-50 rounded text-xs text-purple-700">
                      <Sparkles className="w-3 h-3 inline mr-1" />
                      AI Info: {builder.ai_fetched_info.substring(0, 150)}...
                    </div>
                  )}

                  {/* Social Links */}
                  {(builder.website || builder.linkedin || builder.twitter) && (
                    <div className="flex items-center gap-3 pt-2 border-t">
                      {builder.website && (
                        <a
                          href={builder.website.startsWith('http') ? builder.website : `https://${builder.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Globe className="w-4 h-4" />
                          Website
                        </a>
                      )}
                      {builder.linkedin && (
                        <a
                          href={builder.linkedin.startsWith('http') ? builder.linkedin : `https://${builder.linkedin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Linkedin className="w-4 h-4" />
                          LinkedIn
                        </a>
                      )}
                      {builder.twitter && (
                        <a
                          href={builder.twitter.startsWith('http') ? builder.twitter : `https://twitter.com/${builder.twitter}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Twitter className="w-4 h-4" />
                          Twitter
                        </a>
                      )}
                    </div>
                  )}

                  {/* Consent Progress */}
                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-600">Member Consent</span>
                      <span className={`font-semibold ${
                        builder.consent_percentage >= 51 ? "text-emerald-700" : "text-slate-700"
                      }`}>
                        {builder.consent_percentage?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2 mb-3">
                      <div 
                        className={`h-2 rounded-full ${
                          builder.consent_percentage >= 51 ? "bg-emerald-600" : "bg-amber-500"
                        }`}
                        style={{ width: `${Math.min(builder.consent_percentage || 0, 100)}%` }}
                      />
                    </div>
                    <Button
                      onClick={() => handleGiveConsent(builder.builder_id)}
                      disabled={givingConsent === builder.builder_id}
                      className="w-full bg-orange-600 hover:bg-orange-700"
                    >
                      {givingConsent === builder.builder_id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ThumbsUp className="w-4 h-4 mr-2" />
                      )}
                      Give EOA Consent
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
